import { BlissSVGBuilder } from "bliss-svg-builder";
import { BlissColorHelper as BColor } from "@blissary/utils";

// Default settings for main SVG rendering
const DEFAULT_SETTINGS = "grid=1;stroke-width=0.5;grid-stroke-width=0.16;margin=0.75";

// Compact settings for derivation mini SVGs
const MINI_SETTINGS =
  "grid=0;stroke-width=0.66;grid-stroke-width=0.3;margin=1;" +
  "grid-earth-stroke-width=0.3;grid-sky-stroke-width=0.3;" +
  "grid-major-stroke-width=0;grid-medium-stroke-width=0;grid-minor-stroke-width=0;" +
  "grid-earth-color=#9999ff;grid-sky-color=#5555ff;" +
  "grid-major-color=#c0c0c0;grid-medium-color=#dddddd";

// Cropped settings for shape search buttons
const SHAPE_BUTTON_SETTINGS = "grid=0;crop-top=8;crop-bottom=4;stroke-width=0.5;height=86;margin=1";

/**
 * Creates a simple SVG element with centered text, used for error/missing states.
 */
function createErrorSvg(text, fill, fontSize) {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", "0 0 100 100");
  svg.setAttribute("width", "100");
  svg.setAttribute("height", "100");
  const textEl = document.createElementNS("http://www.w3.org/2000/svg", "text");
  textEl.setAttribute("x", "50");
  textEl.setAttribute("y", "50");
  textEl.setAttribute("text-anchor", "middle");
  textEl.setAttribute("dominant-baseline", "middle");
  textEl.setAttribute("fill", fill);
  textEl.setAttribute("font-size", String(fontSize));
  textEl.textContent = text;
  svg.appendChild(textEl);
  return svg;
}

/**
 * Resolves background settings string for the given color scheme and part-of-speech.
 */
function getBackgroundSetting(colorScheme, pos) {
  if (colorScheme === "Nordic") {
    return BColor.getBlissTextSettingForNordicBackgroundByPos(pos);
  }
  if (colorScheme === "Canadian") {
    return BColor.getBlissTextSettingForCanadianBackgroundByPos(pos);
  }
  return undefined;
}

/**
 * Build a full-size Bliss SVG element with color-scheme background and grid.
 *
 * @param {string} code           Bliss code string
 * @param {string} pos            Part-of-speech key (e.g. "noun", "verb")
 * @param {string} colorScheme    "Nordic" | "Canadian" | undefined
 * @param {string} [settings]     Override default grid settings
 * @param {boolean} [overrideGrid] When true, only the background color is kept from the scheme
 * @returns {SVGElement}
 */
export function buildBlissSvg(code, pos, colorScheme, settings, overrideGrid) {
  if (!code) {
    return createErrorSvg("no code", "#999", 12);
  }

  let backgroundSetting = getBackgroundSetting(colorScheme, pos);

  settings = settings || DEFAULT_SETTINGS;

  if (overrideGrid && backgroundSetting) {
    backgroundSetting = backgroundSetting.split(";")[0];
  }

  const allSettings = backgroundSetting ? `${settings};${backgroundSetting}` : settings;

  try {
    return new BlissSVGBuilder(`[${allSettings}]||${code}`).svgElement;
  } catch (error) {
    console.error(`Error rendering Bliss code "${code}":`, error.message);
    return createErrorSvg("\u26a0", "#cc0000", 50);
  }
}

/**
 * Build a compact mini SVG for derivation displays.
 *
 * @param {string} code         Bliss code string
 * @param {string} colorScheme  "Nordic" | "Canadian" | undefined
 * @returns {SVGElement}
 */
export function buildMiniBlissSvg(code, colorScheme) {
  return buildBlissSvg(code, "", colorScheme, MINI_SETTINGS, true);
}

/**
 * Build a cropped SVG for shape search buttons.
 *
 * @param {string} shapeCode  Bliss shape code (before "=>" in shape data)
 * @returns {SVGElement}
 */
export function buildShapeButtonSvg(shapeCode) {
  return new BlissSVGBuilder(`[${SHAPE_BUTTON_SETTINGS}]||${shapeCode}`).svgElement;
}
